import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import type { SubrouteComponent } from '../../types';
import { StyledExecutionsTableWrapper } from '../../styles';
import {
  PaginationWrapper,
  StyledExecutionData,
  StyledSwitchWrapper,
  StyledTableScrollContainer,
  TableWrapper,
} from './styles';
import SmartConnectorService from '__services/SmartConnectorService';
import TablePagination from '__components/Tables/Big/TablePagination';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import { TRow } from '__components/Kizen/Table';
import BasicTable from '__components/Tables/Basic';
import { useColumns } from './columns';
import { DEFAULT_PAGINATION_SETTINGS, EXECUTION_ACTIONS } from './constants';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import useModal from '__components/Modals/useModal';
import type { SmartConnectorExecution } from 'pages/SmartConnectors/types';
import BasicModal from '__components/Modals/presets/BasicModal';
import { StyledSearchInput, StyledTableToolbar } from '../../styles';
import { KizenTypography } from '__app/typography';
import Input from '__components/Kizen/Input';
import { useToast, toastVariant } from '__components/ToastProvider';
import { getErrorMessage } from 'hooks/useErrors';
import { getOriginalError } from 'services/AxiosService';
import { Button } from '@kizen/kds/Button';
import { downloadFile } from 'utility/DownloadFile';
import { ViewSelector, ViewVariant } from './ViewSelector';
import { useRunSmartConnectorModal } from 'pages/SmartConnectors/components/RunSmartConnectorModal/useRunSmartConnectorModal';
import { RunSmartConnectorModal } from 'pages/SmartConnectors/components/RunSmartConnectorModal';
import { ExecutionProgressModal } from './ExecutionProgressModal';
import { getSearchParam, setSearchParams } from 'hooks/useSearchParam';
import {
  DRY_RUN_EXECUTION_SEARCH_PARAM,
  EXECUTION_ID_SEARCH_PARAM,
} from 'pages/SmartConnectors/constants';

const fileByAction: Record<
  string,
  keyof Pick<
    SmartConnectorExecution,
    'input_file' | 'final_report' | 'sql_output_zip'
  >
> = {
  [EXECUTION_ACTIONS.download_input]: 'input_file',
  [EXECUTION_ACTIONS.download_output]: 'sql_output_zip',
  [EXECUTION_ACTIONS.download_report]: 'final_report',
};

const EMPTY_ARRAY: SmartConnectorExecution[] = [];

export const ExecutionsTable: SubrouteComponent = ({
  smartConnector,
  access,
  runKey,
  metaData,
}) => {
  const { t } = useTranslation();
  const [showToats] = useToast();
  const history = useHistory();
  const {
    page,
    pageSize,
    ordering,
    search,
    actualSearch,
    handleChangePage,
    handleChangePageSize,
    handleChangeSearch,
    headData,
  } = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);

  const [includeDryRun, setIncludeDryRun] = useState(
    () =>
      getSearchParam(history.location, DRY_RUN_EXECUTION_SEARCH_PARAM) ===
      'true'
  );

  const [
    { value: { results = EMPTY_ARRAY, count = 0 } = {}, loading },
    fetchExecutions,
  ] = useAsyncFn(async () => {
    const { data } = await SmartConnectorService.getSmartConnectorExecutions(
      smartConnector.id!,
      {
        params: {
          page,
          page_size: pageSize,
          ordering,
          search,
          include_dry_run: includeDryRun,
          ids: getSearchParam(history.location, EXECUTION_ID_SEARCH_PARAM),
        },
      }
    );
    return data as {
      results: SmartConnectorExecution[];
      count: number;
    };
  }, [
    page,
    pageSize,
    ordering,
    search,
    includeDryRun,
    smartConnector.id,
    runKey,
  ]);

  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  useEffect(() => {
    setSearchParams(
      history,
      {
        [DRY_RUN_EXECUTION_SEARCH_PARAM]: null,
        [EXECUTION_ID_SEARCH_PARAM]: null,
      },
      { method: 'replace' }
    );
  }, [history]);

  const { runSmartConnectorModalProps, showRunSmartConnectorModal } =
    useRunSmartConnectorModal(fetchExecutions);

  const [detailsModalProps, , { show: showDetailsModalProp }] = useModal();
  const [statusModalProps, , { show: showStatusModalProp }] = useModal();

  const [smartConnectorExecution, setSmartConnectorExecution] =
    useState<SmartConnectorExecution | null>(null);

  const onSelectAction = useCallback(
    async (
      { value }: { value: keyof typeof EXECUTION_ACTIONS },
      data: SmartConnectorExecution
    ) => {
      if (value === EXECUTION_ACTIONS.view) {
        showDetailsModalProp();
        setSmartConnectorExecution(data);
      }
      if (fileByAction[value]) {
        const href = data[fileByAction[value]]?.url;
        if (href) {
          downloadFile(href);
        }
      }
      if (value === EXECUTION_ACTIONS.cancel) {
        try {
          await SmartConnectorService.cancelSmartConnectorExecution({
            execution_flow_id: data.id,
          });
          fetchExecutions();
          showToats({
            message: t('Execution has been canceled'),
            variant: toastVariant.SUCCESS,
          });
        } catch (error) {
          showToats({
            message:
              getErrorMessage(getOriginalError(error)) ||
              t('Failed to cancel execution'),
            variant: toastVariant.FAILURE,
          });
        }
      }
      if (
        value === EXECUTION_ACTIONS.reprocess ||
        value === EXECUTION_ACTIONS.reprocess_dry_run
      ) {
        showRunSmartConnectorModal({
          smartConnector,
          execution: data,
          isDryRun: value === EXECUTION_ACTIONS.reprocess_dry_run,
          canSkipDiffCheck: smartConnector.is_diff_check_enabled,
        });
      }
    },
    [
      showDetailsModalProp,
      showToats,
      fetchExecutions,
      smartConnector,
      showRunSmartConnectorModal,
      t,
    ]
  );

  const onViewDetails = useCallback(
    (data: SmartConnectorExecution) => {
      showStatusModalProp();
      setSmartConnectorExecution(data);
    },
    [showStatusModalProp]
  );

  const columns = useColumns({
    onSelectAction,
    onViewDetails,
    access,
    metaData,
  });

  const handleToggleDryRun = (view: ViewVariant) => {
    setIncludeDryRun(view === 'dry');
  };

  return (
    <>
      <StyledExecutionsTableWrapper>
        <StyledTableToolbar>
          <StyledSearchInput
            pill
            type={Input.INPUT_TYPES.SEARCH}
            placeholder={t('Find Executions')}
            value={actualSearch}
            onChange={handleChangeSearch}
          />
          <KizenTypography as="h2" type="header">
            {t('{{count}} Executions', {
              count,
            })}
          </KizenTypography>
          <StyledSwitchWrapper>
            <ViewSelector
              view={includeDryRun ? 'dry' : 'live'}
              onChange={handleToggleDryRun}
            />
            <Button
              leftIcon="action-refresh"
              variant="text"
              onClick={fetchExecutions}
              color={loading ? 'inherit' : 'secondary'}
              disabled={loading}
            />
          </StyledSwitchWrapper>
        </StyledTableToolbar>
        <TableWrapper>
          <StyledTableScrollContainer bottom top gradient={<Gradient />}>
            <BasicTable
              stickyHeader
              head={<TRow head columns={columns} data={headData} />}
              data-qa="smartConnector-executions-table"
              staleData={loading}
            >
              {count ? null : <TRow />}
              {results.map((data) => (
                <TRow key={data.id} columns={columns} data={data} />
              ))}
            </BasicTable>
          </StyledTableScrollContainer>
        </TableWrapper>
        <PaginationWrapper>
          <TablePagination
            page={page}
            perPage={pageSize}
            totalCount={count}
            onChangePage={handleChangePage}
            onChangePerPage={handleChangePageSize}
          />
        </PaginationWrapper>
      </StyledExecutionsTableWrapper>
      {detailsModalProps.show ? (
        <BasicModal
          {...detailsModalProps}
          heading={t('Execution Details')}
          leftBtn={null}
          buttonText={t('Close')}
          actionBtnColor="blue"
          size="medium"
        >
          <StyledExecutionData>
            {JSON.stringify(smartConnectorExecution || {}, null, 2)}
          </StyledExecutionData>
        </BasicModal>
      ) : null}
      {statusModalProps.show ? (
        <ExecutionProgressModal
          {...statusModalProps}
          show
          stepProgress={smartConnectorExecution?.step_progress || []}
          metaData={metaData}
          isDryRun={smartConnectorExecution?.is_dry_run || false}
        />
      ) : null}
      {runSmartConnectorModalProps.show ? (
        <RunSmartConnectorModal {...runSmartConnectorModalProps} />
      ) : null}
    </>
  );
};
